import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { DownloadIcon, FileTextIcon } from 'lucide-react';
import { getSpeechText, uploadSpeechFile } from '../../../services/SpeechToTextService';
import { TranscriptResponseItem } from '../../../types/SpeechToText';
import { useParams } from 'react-router-dom';
import useAsyncEffect from '../../../hooks/useAsyncEffect';
import { State } from '../../../types/State';
import { useTranslate } from 'sbx-crm-menu-ui';

type Props = {
    className?: string;
    file?: File
    transcription: string;
    setTranscription: (transcription: string) => void;
    setLoading: (state: State) => void;
};
const SpeechManageTranscriptionComponent = ({
                                                className = '',
                                                file,
                                                transcription,
                                                setTranscription,
                                                setLoading,
                                            }: Props) => {
    const { job_id } = useParams();
    const intervalId = useRef<NodeJS.Timeout | null>(null);
    const { t: common } = useTranslate('common');
    const { t: speech } = useTranslate('speech');
    const [isTranscribing, setIsTranscribing] = useState<boolean>(false);
    const [transcriptionState, setTranscriptionState] = useState('');
    const [fileName, setFileName] = useState('');


    useAsyncEffect(async () => {
        if (job_id && job_id !== 'create') {
            setLoading(State.PENDING);
            await getTextService(job_id);
            setLoading(State.RESOLVED);
        }
    }, [job_id]);

    const getTextService = async (jobId: string) => {
        if (intervalId.current) return; // Evita crear múltiples intervalos

        const checkSpeechTextStatus = async () => {
            try {
                const textResponse = await getSpeechText(jobId);

                // if (textResponse?.item?.error) {
                //     setError(textResponse.item.error);
                // }

                if (textResponse?.item?.state) {
                    setTranscriptionState(textResponse.item.state);
                }

                if (textResponse?.item && textResponse?.item?.state !== 'IN_PROGRESS') {
                    setIsTranscribing(false);
                    setFileName(textResponse?.item?.name || '');
                    setTranscription(textResponse?.item?.text || '');
                    clearInterval(intervalId.current as unknown as NodeJS.Timeout);
                    intervalId.current = null;
                }
            } catch (error) {
                console.error('Error fetching speech text status:', error);
                clearInterval(intervalId.current as unknown as NodeJS.Timeout);
                intervalId.current = null;

            }
        };


        checkSpeechTextStatus();


        intervalId.current = setInterval(checkSpeechTextStatus, 5000 * 6);
    };

    useEffect(() => {
        setTranscriptionState('');
        setTranscription('')
        setFileName('');
    }, [file]);

    const handleGetTranscription = async () => {
        if (!file) return;
        setIsTranscribing(true);
        // Simulating transcription process
        const response = await uploadSpeechFile(file);
        if (response?.item?.state) {
            setTranscriptionState(response.item.state);
        }
        if (response?.success && response?.item) {
            const item = response.item as TranscriptResponseItem;
            if (item?.job_id) {
                await getTextService(item.job_id);
            } else {
                setIsTranscribing(false);
            }
        } else {
            setIsTranscribing(false);
        }
    };

    const handleDownload = () => {
        if (!transcription) return;
        const element = document.createElement('a');
        const file = new Blob([transcription], { type: 'text/plain' });
        element.href = URL.createObjectURL(file);
        element.download = 'transcription.txt';
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);
    };

    return (
        <Card className={className}>
            <CardHeader>
                <CardTitle>{speech("file_status")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 h-64 flex flex-col justify-between">
                <div>
                    <p>
                        <strong>{common("file")}:</strong> {file ? file.name : (fileName || 'No file uploaded')}
                    </p>
                    <p>
                        <strong>{speech("size")}:</strong> {file ? `${(file.size / 1024 / 1024).toFixed(2)} MB` : 'N/A'}
                    </p>

                    {transcriptionState && <p className="capitalize">
                        <strong>{common("status")}:</strong> {transcriptionState.toLowerCase().split('_').join(' ')}
                    </p>}
                    {/*{error && <p>*/}
                    {/*    <strong*/}
                    {/*        className="text-red-500">Size:</strong> {file ? `${(file.size / 1024 / 1024).toFixed(2)} MB` : 'N/A'}*/}
                    {/*</p>}*/}
                </div>
                <div className="flex flex-col space-y-2">
                    <Button
                        onClick={handleGetTranscription}
                        disabled={!file || isTranscribing}
                        className="w-full  !bg-black !text-white rounded "
                    >
                        <FileTextIcon className="mr-2 h-4 w-4" />
                        {isTranscribing ? speech("transcribing") + '...' : speech("get_transcription")}
                    </Button>
                    <Button
                        onClick={handleDownload}
                        disabled={!transcription}
                        className="w-full !bg-black !text-white !rounded"
                    >
                        <DownloadIcon className="mr-2 h-4 w-4" />
                        {speech('download_transcription')}
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
};

export default SpeechManageTranscriptionComponent;